/**
 * S6.2 batch-capture mutations — store-first, same contract as
 * `useShipmentBatchMutations` (S6.1): update the Zustand list echo, then
 * invalidate every cache a batch mutation can stale (shipping lists, OCC
 * projections, 進貨 prefill) via `invalidateShippingCaches`.
 *
 * Slice-local hook home: `src/hooks/useShipping.ts` is owned by a parallel
 * slice — fold these mutations in when that seam opens (slice follow-up).
 */

import { invalidateShippingCaches } from '@/hooks/useShipping';
import { useShippingStore } from '@/stores/shippingStore';
import type { ShipmentBatchCapture } from './batchCapture';
import batchCaptureApi from './batchCapture.api';
import type {
  CorrectBatchQuantitiesRequest,
  CreateCapturedBatchRequest,
  CutOffCapturedBatchRequest,
} from './batchCapture.api';

export function useBatchCaptureMutations() {
  const { upsertBatchInList } = useShippingStore();

  const createCapturedBatch = async (
    body: CreateCapturedBatchRequest
  ): Promise<ShipmentBatchCapture> => {
    const response = await batchCaptureApi.createBatch(body);
    upsertBatchInList(response.data);
    invalidateShippingCaches();
    return response.data;
  };

  /** 封櫃截單 with the capture grid — loaded qty is FIXED here (S6.1 gate). */
  const cutOffCapturedBatch = async (
    id: string,
    body: CutOffCapturedBatchRequest
  ): Promise<ShipmentBatchCapture> => {
    const response = await batchCaptureApi.cutOffBatch(id, body);
    upsertBatchInList(response.data);
    invalidateShippingCaches();
    return response.data;
  };

  /** 改數量 correction loop — reason-logged; OCC short-load facts re-derive. */
  const correctBatchQuantities = async (
    id: string,
    body: CorrectBatchQuantitiesRequest
  ): Promise<ShipmentBatchCapture> => {
    const response = await batchCaptureApi.correctQuantities(id, body);
    upsertBatchInList(response.data);
    invalidateShippingCaches();
    return response.data;
  };

  return { createCapturedBatch, cutOffCapturedBatch, correctBatchQuantities };
}
